import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  tenantConfigurationService,
  TenantDetailDto,
  TenantUserDto,
  UpdateTenantRequest,
  AddUserToTenantRequest,
} from '../../services/tenantConfigurationService';
import { teamService, TeamDto } from '../../services/teamService';
import { TeamsManagement } from './TeamsManagement';
import { apiErrorMessage } from '../../utils/apiUtils';

const VALID_ROLES = ['Agent', 'Supervisor', 'TenantAdmin'];

/**
 * Roles a team assignment actually narrows. Must stay in step with the role check in
 * `TenantMiddleware`, which skips team resolution for TenantAdmin and PlatformAdmin only —
 * an Agent with a TeamKey IS scoped by it, on top of their own operator alias.
 */
const TEAM_SCOPED_ROLES = ['Supervisor', 'Agent'];

// IANA time-zone list from the Intl Enumeration API. The backend is the authoritative validator
// so a typo can never silently persist even if this list is empty.
const IANA_TIME_ZONES: string[] = (() => {
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
})();
const IANA_TIME_ZONES_SET = new Set(IANA_TIME_ZONES);

export const TenantConfiguration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant');

  const [tenant, setTenant] = useState<TenantDetailDto | null>(null);
  const [users, setUsers] = useState<TenantUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateTenantRequest | null>(null);

  const [removeConfirmUser, setRemoveConfirmUser] = useState<TenantUserDto | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState<AddUserToTenantRequest>({ userId: '', role: 'Supervisor', displayName: '', email: '', operatorAlias: '' });
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [addUserConfirm, setAddUserConfirm] = useState<{ userId: string; tenantId: string } | null>(null);
  const [editingAlias, setEditingAlias] = useState<{ userId: string; value: string } | null>(null);
  const [savingAlias, setSavingAlias] = useState(false);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [savingTeamFor, setSavingTeamFor] = useState<string | null>(null);

  const tzOptions = useMemo(
    () =>
      editForm?.timeZoneId && !IANA_TIME_ZONES_SET.has(editForm.timeZoneId)
        ? [editForm.timeZoneId, ...IANA_TIME_ZONES]
        : IANA_TIME_ZONES,
    [editForm?.timeZoneId]
  );

  useEffect(() => {
    if (tenantId) {
      loadTenantData(tenantId);
    }
  }, [tenantId]);

  const loadTenantData = async (id: string) => {
    try {
      setLoading(true);
      const [detail, userList] = await Promise.all([
        tenantConfigurationService.getTenantDetail(id),
        tenantConfigurationService.getTenantUsers(id),
      ]);
      setTenant(detail);
      setUsers(userList);
      // Loaded separately because a tenant may legitimately have no teams. A failure here is NOT
      // that case: swallowing it rendered "this agency has no teams" over a 403 or a transport
      // error, on the control that decides which recordings a Supervisor can read.
      try {
        setTeams(await teamService.getAll());
        setTeamsError(null);
      } catch (err: unknown) {
        setTeams([]);
        setTeamsError(apiErrorMessage(err, 'Teams could not be loaded'));
      }
      setEditForm({
        displayName: detail.displayName,
        sftpUsername: detail.sftpUsername,
        timeZoneId: detail.timeZoneId,
        seatLimit: detail.seatLimit,
        dailyManualUploadLimit: detail.dailyManualUploadLimit,
        dailyBatchLimit: detail.dailyBatchLimit,
        maxAudioDurationMinutes: detail.maxAudioDurationMinutes,
        isActive: detail.isActive,
        contractStartDate: detail.contractStartDate.split('T')[0],
        contractEndDate: detail.contractEndDate.split('T')[0],
      });
      setError(null);
    } catch {
      setError('Failed to load tenant configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTenant = async () => {
    if (!tenantId || !editForm) return;
    try {
      setSaving(true);
      await tenantConfigurationService.updateTenant(tenantId, editForm);
      setSuccess('Tenant configuration saved');
      await loadTenantData(tenantId);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to save tenant configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!tenantId) return;
    try {
      await tenantConfigurationService.updateUserRole(userId, tenantId, newRole);
      setSuccess(`Role updated to ${newRole}`);
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, role: newRole } : u));
    } catch {
      setError('Failed to update role');
    }
  };

  const handleActiveToggle = async (userId: string, isActive: boolean) => {
    if (!tenantId) return;
    try {
      await tenantConfigurationService.updateUserActive(userId, tenantId, isActive);
      const action = isActive ? 'activated' : 'deactivated';
      setSuccess(`User ${action}`);
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, isActive } : u));
    } catch {
      setError('Failed to update user status');
    }
  };

  const handleSaveAlias = async (userId: string, alias: string) => {
    if (!tenantId) return;
    try {
      setSavingAlias(true);
      await tenantConfigurationService.updateOperatorAlias(userId, tenantId, alias);
      setSuccess('Operator alias updated');
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, operatorAlias: alias } : u));
      setEditingAlias(null);
    } catch {
      setError('Failed to update operator alias');
    } finally {
      setSavingAlias(false);
    }
  };

  const handleTeamChange = async (userId: string, teamKey: string) => {
    if (!tenantId) return;
    const previous = users.find(u => u.userId === userId)?.teamKey ?? null;
    const next = teamKey || null;
    try {
      setSavingTeamFor(userId);
      // Optimistic, then reconciled by the catch below. Without holding the value in state the
      // dropdown snapped back to "Agency-wide" on every successful save, because the roster DTO
      // is the only thing that knows the stored assignment.
      setUsers(prev => prev.map(u => (u.userId === userId ? { ...u, teamKey: next } : u)));
      await teamService.updateUserTeam(userId, tenantId, next);
      setSuccess(next ? 'Team assignment updated' : 'Team assignment cleared — this user is now agency-wide');
    } catch (err: unknown) {
      setUsers(prev => prev.map(u => (u.userId === userId ? { ...u, teamKey: previous } : u)));
      // The server's own message matters here: an unknown or inactive team comes back as a 400
      // naming the key, which a generic string would have hidden.
      setError(apiErrorMessage(err, 'Failed to update team assignment'));
    } finally {
      setSavingTeamFor(null);
    }
  };

  const reloadTeams = async () => {
    try {
      setTeams(await teamService.getAll());
      setTeamsError(null);
    } catch (err: unknown) {
      setTeamsError(apiErrorMessage(err, 'Teams could not be loaded'));
    }
  };

  const handleRemoveUser = async () => {
    if (!tenantId || !removeConfirmUser) return;
    try {
      setRemoveLoading(true);
      await tenantConfigurationService.removeUserFromTenant(tenantId, removeConfirmUser.userId);
      setSuccess(`${removeConfirmUser.displayName || 'User'} removed from tenant`);
      setUsers(prev => prev.filter(u => u.userId !== removeConfirmUser.userId));
      setRemoveConfirmUser(null);
    } catch {
      setError('Failed to remove user');
      setRemoveConfirmUser(null);
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!tenantId) return;
    try {
      setAddUserSaving(true);
      await tenantConfigurationService.addUserToTenant(tenantId, addUserForm);
      setAddUserConfirm({ userId: addUserForm.userId, tenantId });
      setAddUserOpen(false);
      setAddUserForm({ userId: '', role: 'Supervisor', displayName: '', email: '', operatorAlias: '' });
      await loadTenantData(tenantId);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to add user'));
      setAddUserOpen(false);
    } finally {
      setAddUserSaving(false);
    }
  };

  if (!tenantId) {
    return <Alert severity="info">Select a tenant from the Platform Administration page.</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant || !editForm) {
    return <Alert severity="error">Tenant not found.</Alert>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Tenant Details */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {tenant.displayName} ({tenant.tenantId})
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <TextField
          label="Display Name"
          value={editForm.displayName}
          onChange={e => setEditForm({ ...editForm, displayName: e.target.value })}
          fullWidth
        />
        <TextField
          label="SFTP Username"
          value={editForm.sftpUsername}
          onChange={e => setEditForm({ ...editForm, sftpUsername: e.target.value })}
          fullWidth
        />
        <Autocomplete
          freeSolo
          disableClearable
          options={tzOptions}
          value={editForm.timeZoneId}
          onChange={(_, value) => setEditForm({ ...editForm, timeZoneId: (value as string) ?? '' })}
          onInputChange={(_, value) => setEditForm({ ...editForm, timeZoneId: value })}
          fullWidth
          renderInput={params => (
            <TextField
              {...params}
              label="Time Zone"
              placeholder="America/New_York"
              helperText="IANA id — governs the SFTP daily-quota reset (customer's local midnight)"
            />
          )}
        />
        <TextField
          label="Seat Limit"
          type="number"
          value={editForm.seatLimit}
          onChange={e => setEditForm({ ...editForm, seatLimit: parseInt(e.target.value) || 0 })}
          fullWidth
        />
        <TextField
          label="Daily Manual Upload Limit"
          type="number"
          value={editForm.dailyManualUploadLimit}
          onChange={e => setEditForm({ ...editForm, dailyManualUploadLimit: parseInt(e.target.value) || 0 })}
          fullWidth
        />
        <TextField
          label="Daily Batch Limit"
          type="number"
          value={editForm.dailyBatchLimit}
          onChange={e => setEditForm({ ...editForm, dailyBatchLimit: parseInt(e.target.value) || 0 })}
          fullWidth
        />
        <TextField
          label="Max Audio Duration (min)"
          type="number"
          value={editForm.maxAudioDurationMinutes}
          onChange={e => setEditForm({ ...editForm, maxAudioDurationMinutes: parseInt(e.target.value) || 0 })}
          fullWidth
        />
        <TextField
          label="Contract Start"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={editForm.contractStartDate}
          onChange={e => setEditForm({ ...editForm, contractStartDate: e.target.value })}
          fullWidth
        />
        <TextField
          label="Contract End"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={editForm.contractEndDate}
          onChange={e => setEditForm({ ...editForm, contractEndDate: e.target.value })}
          fullWidth
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={editForm.isActive}
            onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })}
          />
        }
        label="Tenant Active"
      />

      {/* Read-only masked connection strings */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <TextField
          label="Database Connection (masked)"
          value={tenant.maskedDatabaseConnection}
          fullWidth
          margin="normal"
          InputProps={{ readOnly: true }}
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
        <TextField
          label="Storage Connection (masked)"
          value={tenant.maskedStorageConnection}
          fullWidth
          margin="normal"
          InputProps={{ readOnly: true }}
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Created: {new Date(tenant.createdDate).toLocaleString()}
        {tenant.modifiedDate && ` • Modified: ${new Date(tenant.modifiedDate).toLocaleString()}`}
      </Typography>

      <Button
        variant="contained"
        onClick={handleSaveTenant}
        disabled={saving}
        sx={{ mb: 3 }}
      >
        {saving ? <CircularProgress size={20} aria-label="Saving" /> : 'Save Configuration'}
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* User Roster */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          Users ({users.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddUserOpen(true)}
        >
          Add User
        </Button>
      </Box>

      {/* Named rather than implied: without it, a failed teams load is indistinguishable from an
          agency that simply has no teams, and the Team column silently reads "—" for everyone. */}
      {teamsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTeamsError(null)}>
          {teamsError} — the Team column cannot be shown or changed until this is resolved.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Operator Alias</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.userId} sx={{ opacity: user.isActive ? 1 : 0.5 }}>
                <TableCell>{user.displayName || '(not synced)'}</TableCell>
                <TableCell>
                  {user.email || '—'}
                  {user.email && user.emailSource !== 'SignIn' && (
                    /*
                     * Text carries the meaning, colour only reinforces it — an unverified address
                     * is not an error, it is a state with a consequence: it cannot receive the
                     * scheduled digest unless its domain is allow-listed. Without this the roster
                     * gives an administrator no way to answer "why is this person not getting
                     * reports".
                     *
                     * MUI Tooltip rather than a raw `title` attribute, matching the three other
                     * tooltips in this file: the explanation IS the feature here, and a native
                     * title does not surface on keyboard focus, is announced inconsistently by
                     * screen readers, and cannot be dismissed (WCAG 2.2 AA 1.4.13).
                     */
                    <Tooltip title="This address was entered by GovIQ staff and has not been confirmed by a sign-in. It cannot receive the scheduled QA digest unless its mail domain is on the tenant's allow-list. It is confirmed automatically the next time this user signs in.">
                      <Chip label="Unverified" size="small" variant="outlined" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={user.role}
                    size="small"
                    onChange={e => handleRoleChange(user.userId, e.target.value)}
                    sx={{ minWidth: 130 }}
                  >
                    {VALID_ROLES.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  {/* Which roles a team actually scopes is decided in TenantMiddleware, and this
                      column must not claim otherwise. It skips only TenantAdmin and PlatformAdmin,
                      so a team applies to Supervisors AND Agents — an Agent is narrowed to their
                      own alias within the team, and an empty or inactive team shows them nothing.
                      The dropdown is therefore rendered for both, which is also the only way to
                      clear a key left behind when a Supervisor is demoted in the column alongside. */}
                  {TEAM_SCOPED_ROLES.includes(user.role) ? (
                    teamsError ? (
                      // The list failed to load, so no assignment can be offered — but the stored
                      // value is still in force and saying "Agency-wide" here would be a lie about
                      // an access control.
                      <Typography variant="body2" color="text.secondary">
                        {user.teamKey ? `${user.teamKey} (unchangeable — see above)` : '—'}
                      </Typography>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id={`team-label-${user.userId}`}>Team</InputLabel>
                        <Select
                          labelId={`team-label-${user.userId}`}
                          id={`team-${user.userId}`}
                          label="Team"
                          value={user.teamKey ?? ''}
                          aria-busy={savingTeamFor === user.userId}
                          // Non-interactive while its own save is in flight: the update is
                          // optimistic and reconciles on the response, so a second change fired
                          // before the first resolved would race two writes to an access-control
                          // field. Keyed on this user's id so other rows stay editable.
                          disabled={savingTeamFor === user.userId}
                          onChange={e => handleTeamChange(user.userId, e.target.value)}
                        >
                          <MenuItem value="">Agency-wide</MenuItem>
                          {teams.filter(t => t.isActive).map(t => (
                            <MenuItem key={t.teamKey} value={t.teamKey}>{t.name}</MenuItem>
                          ))}
                          {/* A team that has since been deactivated is still the stored assignment,
                              and it still scopes this user — to nothing. Shown so the roster never
                              reads "Agency-wide" for somebody who sees no calls, but disabled:
                              re-selecting it is the one choice the server always refuses. */}
                          {user.teamKey && !teams.some(t => t.teamKey === user.teamKey && t.isActive) && (
                            <MenuItem value={user.teamKey} disabled>
                              {teams.find(t => t.teamKey === user.teamKey)?.name ?? user.teamKey} (inactive)
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    )
                  ) : user.teamKey ? (
                    // TenantAdmin and PlatformAdmin administer the whole agency, so the middleware
                    // genuinely does not apply a stored key to them. Naming the role is the point:
                    // promote a scoped Supervisor and this is what the assignment becomes.
                    <Typography variant="body2" color="text.secondary">
                      {user.teamKey} (not applied to {user.role})
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {editingAlias?.userId === user.userId ? (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        value={editingAlias.value}
                        onChange={e => setEditingAlias({ ...editingAlias, value: e.target.value })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveAlias(user.userId, editingAlias.value);
                          if (e.key === 'Escape') setEditingAlias(null);
                        }}
                        placeholder="e.g. JSMITH,John Smith"
                        sx={{ minWidth: 160 }}
                        autoFocus
                        disabled={savingAlias}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveAlias(user.userId, editingAlias.value)}
                        disabled={savingAlias}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        {savingAlias ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setEditingAlias(null)}
                        disabled={savingAlias}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        ✕
                      </Button>
                    </Box>
                  ) : (
                    <Tooltip title="Click to edit — must match the operator name parsed from audio filenames">
                      <Chip
                        label={user.operatorAlias || '(not set)'}
                        size="small"
                        variant={user.operatorAlias ? 'filled' : 'outlined'}
                        color={user.operatorAlias ? 'primary' : 'default'}
                        onClick={() => setEditingAlias({ userId: user.userId, value: user.operatorAlias || '' })}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    color={user.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(user.createdDate).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={user.isActive ? 'Deactivate — suspends access, keeps record' : 'Reactivate'}>
                    <IconButton
                      size="small"
                      onClick={() => handleActiveToggle(user.userId, !user.isActive)}
                      color={user.isActive ? 'default' : 'success'}
                    >
                      {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove — permanently deletes this access record">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setRemoveConfirmUser(user)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No users assigned to this tenant. Use the onboarding script to add users.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 3 }} />

      {/* Teams Management. onTeamsChanged keeps the roster's Team dropdown above in step —
          the two components would otherwise hold independent copies of the same list. */}
      <TeamsManagement onTeamsChanged={reloadTeams} />

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        message={success}
      />

      {/* Remove User Confirmation */}
      <Dialog open={!!removeConfirmUser} onClose={() => setRemoveConfirmUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This permanently removes <strong>{removeConfirmUser?.displayName || removeConfirmUser?.userId}</strong> from
            this tenant. They will immediately lose all access and cannot be re-added without a new entry.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            To temporarily suspend access without deleting the record, use the <strong>Deactivate</strong> button
            (block icon) instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemoveUser} disabled={removeLoading}>
            {removeLoading ? <CircularProgress size={20} /> : 'Remove Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to {tenant?.displayName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste the user's Entra Object ID (OID). The user must already be invited as a B2B guest and have
            <strong> extensionAttribute1</strong> set to <strong>{tenantId}</strong>. Display name and email are
            optional — they auto-populate on first login.
          </Typography>
          <TextField
            label="Entra Object ID (OID)"
            value={addUserForm.userId}
            onChange={e => setAddUserForm({ ...addUserForm, userId: e.target.value.trim() })}
            fullWidth
            required
            margin="normal"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={addUserForm.role}
              onChange={e => setAddUserForm({ ...addUserForm, role: e.target.value })}
            >
              {VALID_ROLES.map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Display Name (optional)"
            value={addUserForm.displayName}
            onChange={e => setAddUserForm({ ...addUserForm, displayName: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Leave blank — auto-filled from Entra on first login"
          />
          <TextField
            label="Email (optional)"
            value={addUserForm.email}
            onChange={e => setAddUserForm({ ...addUserForm, email: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Leave blank — auto-filled from Entra on first login"
          />
          <TextField
            label="Operator Alias"
            value={addUserForm.operatorAlias}
            onChange={e => setAddUserForm({ ...addUserForm, operatorAlias: e.target.value })}
            fullWidth
            margin="normal"
            placeholder="e.g. JSMITH or Liz Rose"
            helperText="Must match the operator name parsed from audio filenames. Required for Agent role to see their calls. Comma-separated for multiple aliases."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={addUserSaving || !addUserForm.userId || !addUserForm.role}
          >
            {addUserSaving ? <CircularProgress size={20} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-add extensionAttribute1 reminder */}
      {addUserConfirm && (
        <Alert
          severity="success"
          onClose={() => setAddUserConfirm(null)}
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            User added. If extensionAttribute1 has not been set yet, run this command:
          </Typography>
          <Box
            component="pre"
            sx={{ fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}
          >
            {`az rest --method PATCH --url "https://graph.microsoft.com/v1.0/users/${addUserConfirm.userId}" --headers "Content-Type=application/json" --body '{"onPremisesExtensionAttributes":{"extensionAttribute1":"${addUserConfirm.tenantId}"}}'`}
          </Box>
        </Alert>
      )}
    </Box>
  );
};
